class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search') || this.querySelector('.predictive-search-results');
    this.searchButton = this.querySelector('.search-toggle-btn');
    this.searchPanel = this.querySelector('.search-panel');
    this.searchOverlay = this.querySelector('.search-overlay');
    this.closeButton = this.querySelector('.search-close-btn');

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    // Toggle Search Panel
    if (this.searchButton && this.searchPanel) {
      this.searchButton.addEventListener('click', (e) => {
        e.preventDefault();
        const isHidden = this.searchPanel.classList.contains('hidden');
        if (isHidden) {
          this.searchPanel.classList.remove('hidden');
          if (this.searchOverlay) {
            this.searchOverlay.classList.remove('opacity-0', 'pointer-events-none');
          }
          setTimeout(() => {
            this.input.focus();
          }, 50);
        } else {
          this.closeSearch();
        }
      });
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeSearch());
    }

    if (this.searchOverlay) {
      this.searchOverlay.addEventListener('click', () => this.closeSearch());
    }

    // Input debounce
    let debounceTimer;
    this.input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.onChange();
      }, 300);
    });

    // Close on click outside
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.closeSearch();
      }
    });
  }

  closeSearch() {
    if (this.searchPanel) {
      this.searchPanel.classList.add('hidden');
    }
    if (this.searchOverlay) {
      this.searchOverlay.classList.add('opacity-0', 'pointer-events-none');
    }
    this.predictiveSearchResults.classList.add('hidden');
    this.input.value = '';
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.add('hidden');
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,article,page&resources[limit]=6`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        const products = data.resources.results.products || [];
        const articles = data.resources.results.articles || [];
        const pages = data.resources.results.pages || [];
        this.renderSearchResults(products, articles, pages, searchTerm);
      })
      .catch((error) => {
        console.error('Error fetching predictive search:', error);
      });
  }

  renderSearchResults(products, articles, pages, searchTerm) {
    this.predictiveSearchResults.classList.remove('hidden');
    
    const totalResults = products.length + articles.length + pages.length;

    if (totalResults === 0) {
      this.predictiveSearchResults.innerHTML = `
        <div class="p-6 text-center text-northern-navy/70 font-body-md">
          No results found for "${window.NMCLI.escapeHtml(searchTerm)}".
        </div>
      `;
      return;
    }

    let resultsHtml = '';

    // Products section
    if (products.length > 0) {
      resultsHtml += `<div class="p-4"><h3 class="font-headline-sm text-northern-navy text-sm uppercase tracking-widest mb-4">Products</h3><ul class="flex flex-col gap-4">`;
      products.forEach((product) => {
        const safeUrl = window.NMCLI.escapeHtml(product.url || '');
        const safeTitle = window.NMCLI.escapeHtml(product.title || '');
        const safeImage = product.image ? window.NMCLI.escapeHtml(product.image) : '';
        const safePrice = window.NMCLI.escapeHtml(String(product.price || ''));
        resultsHtml += `
          <li>
            <a href="${safeUrl}" class="flex items-center gap-4 group hover:bg-surface-bright p-2 rounded-lg transition-colors">
              ${safeImage ? `<img src="${safeImage}" alt="${safeTitle}" class="w-12 h-16 object-cover rounded-md bg-pearl-ivory">` : '<div class="w-12 h-16 bg-pearl-ivory rounded-md"></div>'}
              <div class="flex flex-col">
                <span class="font-body-md text-northern-navy group-hover:text-antique-gold transition-colors">${safeTitle}</span>
                <span class="font-body-md text-northern-navy/70 text-sm">${safePrice}</span>
              </div>
            </a>
          </li>
        `;
      });
      resultsHtml += `</ul></div>`;
    }

    // Articles section
    if (articles.length > 0) {
      resultsHtml += `<div class="p-4 border-t border-northern-navy/10"><h3 class="font-headline-sm text-northern-navy text-sm uppercase tracking-widest mb-4">Articles</h3><ul class="flex flex-col gap-3">`;
      articles.forEach((article) => {
        const safeUrl = window.NMCLI.escapeHtml(article.url || '');
        const safeTitle = window.NMCLI.escapeHtml(article.title || '');
        resultsHtml += `
          <li>
            <a href="${safeUrl}" class="flex items-center gap-3 group hover:bg-surface-bright p-2 rounded-lg transition-colors">
              <span class="material-symbols-outlined text-antique-gold text-lg">article</span>
              <span class="font-body-md text-northern-navy group-hover:text-antique-gold transition-colors text-sm">${safeTitle}</span>
            </a>
          </li>
        `;
      });
      resultsHtml += `</ul></div>`;
    }

    // Pages section
    if (pages.length > 0) {
      resultsHtml += `<div class="p-4 border-t border-northern-navy/10"><h3 class="font-headline-sm text-northern-navy text-sm uppercase tracking-widest mb-4">Pages</h3><ul class="flex flex-col gap-3">`;
      pages.forEach((page) => {
        const safeUrl = window.NMCLI.escapeHtml(page.url || '');
        const safeTitle = window.NMCLI.escapeHtml(page.title || '');
        resultsHtml += `
          <li>
            <a href="${safeUrl}" class="flex items-center gap-3 group hover:bg-surface-bright p-2 rounded-lg transition-colors">
              <span class="material-symbols-outlined text-antique-gold text-lg">description</span>
              <span class="font-body-md text-northern-navy group-hover:text-antique-gold transition-colors text-sm">${safeTitle}</span>
            </a>
          </li>
        `;
      });
      resultsHtml += `</ul></div>`;
    }

    resultsHtml += `
      <div class="border-t border-northern-navy/10 p-4">
        <a href="/search?q=${encodeURIComponent(searchTerm)}&type=product,article,page" class="font-nav-link text-northern-navy uppercase tracking-widest text-xs flex justify-center hover:text-antique-gold transition-colors">
          View all results
        </a>
      </div>
    `;

    this.predictiveSearchResults.innerHTML = resultsHtml;
  }
}

customElements.define('predictive-search', PredictiveSearch);
